import React, { useEffect, useState } from "react";
import { Table, Space, Tooltip, Upload, message, Input } from "antd";
import { Form, Row, Col, Button, Modal } from "react-bootstrap";
import FormLabel from "../../components/Common/FormLabel";
import FormControl from "../../components/Common/FormControl";
import FormControlTextArea from "../../components/Common/FormControlTextArea";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchContentLibraryList,
  resetContentLibraryState,
  createContentLibrary,
  deleteContentLibrary,
  resetDeleteState,
} from "../../redux/Slices/contentLibrarySlice";
import Loader from "../../components/Common/Loader";
import ToastNotification from "../../components/Common/ToastNotification";
import { formatDate } from "../../utils/Utils";

const ContentLibrary = () => {
  const dispatch = useDispatch();
  const { trans } = useCustomTranslation();
  const { profileData } = useSelector((state) => state.profile);

  const {
    contentLibrary,
    isLoading: isContentLibraryLoading,
    error: contentLibraryError,
    updateSuccess,
    updateError,
    lastDeleteMessage,
  } = useSelector((state) => state.contentLibrary);

  const [showModal, setShowModal] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    mediaId: 0,
    title: "",
    language: "en-US",
    description: "",
    fileName: null,
    fileBase64: "",
    mediaType: "",
  });

  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState(contentLibrary);
  const [errors, setErrors] = useState({});
  const [toastConfig, setToastConfig] = useState(null);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [confimationModal, setConfimationModal] = useState(false);

  const coachId = profileData?.coachId;

  useEffect(() => {
    if (coachId) {
      // Fetch content library data when component mounts
      dispatch(fetchContentLibraryList({ coachId }));

      return () => {
        dispatch(resetContentLibraryState());
      };
    }
  }, [dispatch, coachId]);

  useEffect(() => {
    // Update filtered data when contentLibrary is updated
    setFilteredData(contentLibrary);
  }, [contentLibrary]);

  // Handle Add button click
  const handleAdd = () => {
    setIsEditMode(false);
    setFormData({
      coachId: coachId,
      title: "",
      language: "en-US",
      description: "",
      fileName: null,
      fileBase64: "",
      sourceType: "contentLibrary",
      fileType: "",
    });
    setShowModal(true);
  };

  // Handle Edit button click
  const handleEdit = (record) => {
    setFormData({
      coachId: record.coachId,
      mediaId: record.mediaId ? record.mediaId : 0,
      title: record.title,
      language: record.language ? record.language : "en-US",
      description: record.description,
      fileName: record.fileName,
      fileBase64: "",
      sourceType: "contentLibrary",
      fileType: record.fileType ? record.fileType : "",
      mediaType: record.mediaType,
    });
    setIsEditMode(true);
    setShowModal(true);
  };

  const languages = [{ value: "en-US", label: "English-US" }];

  const getLanguageLabel = (languageCode) => {
    const language = languages.find((lang) => lang.value === languageCode);
    return language ? language.label : languageCode;
  };

  // Function to render file viewer icons
  const renderFileViewer = (record) => {
    if (!record?.blobUrl) return null;

    const openPopupWindow = (htmlContent) => {
      const newWindow = window.open("", "_blank");
      if (newWindow) {
        newWindow.document.write(htmlContent);
        newWindow.document.close();
      } else {
        console.error("Popup blocked");
      }
    };

    const handleClick = (e) => {
      e.stopPropagation();

      switch (record.fileType?.toUpperCase()) {
        case "IMAGE":
          openPopupWindow(`
          <html><body style="margin:0">
            <img src="${record.blobUrl}" style="max-width:100%; height:auto;" />
          </body></html>
        `);
          break;

        case "PDF":
          window.open(record.blobUrl, "_blank", "noopener,noreferrer");
          break;

        case "AUDIO":
          openPopupWindow(`
          <html><body style="margin:0; display:flex; align-items:center; justify-content:center; height:100vh;">
            <audio controls autoplay src="${record.blobUrl}" style="width:100%"></audio>
          </body></html>
        `);
          break;

        case "VIDEO":
          openPopupWindow(`
          <html><body style="margin:0">
            <video controls autoplay src="${record.blobUrl}" style="width:100%; height:auto;"></video>
          </body></html>
        `);
          break;

        default:
          window.open(record.blobUrl, "_blank", "noopener,noreferrer");
      }
    };

    return (
      <Tooltip title="View">
        <Button
          type="button"
          onClick={handleClick}
          className="action-buttons pl-0"
        >
          <i className="bi bi-eye" />
        </Button>
      </Tooltip>
    );
  };

  const handleDeleteClick = (record, e) => {
    e.stopPropagation();
    setRecordToDelete(record);
    setConfimationModal(true);
  };
  const columns = [
    {
      title: trans("Common.title"),
      dataIndex: "title",
      key: "title",
    },
    {
      title: trans("Common.type"),
      dataIndex: "fileType",
      key: "fileType",
    },
    {
      title: trans("Common.language"),
      dataIndex: "language",
      key: "language",
      render: (languageCode) => {
        return getLanguageLabel(languageCode);
      },
    },
    {
      title: trans("Common.created"),
      dataIndex: "createdOn",
      key: "createdOn",
      render: (createdOn) => {
        return formatDate(createdOn);
      },
    },
    {
      title: trans("Common.action"),
      key: "action",
      width: "10%",
      render: (text, record) => (
        <>
          <Space size="small">
            <Tooltip title={trans("Common.edit")}>
              <Button
                type="button"
                onClick={() => handleEdit(record)}
                className="action-buttons pl-0"
              >
                <i className="bi bi-pencil"></i>
              </Button>
            </Tooltip>
            <Tooltip title={trans("Common.view")}>
              <div className="exercise-name-wrapper">
                {renderFileViewer(record)}
              </div>
            </Tooltip>
            <Tooltip title={trans("Common.delete")}>
              <Button
                type="button"
                className="action-buttons"
                onClick={(e) => handleDeleteClick(record, e)}
              >
                <i className="bi bi-trash"></i>
              </Button>
            </Tooltip>
          </Space>
        </>
      ),
    },
  ];

  const handleConfirmDelete = () => {
    if (recordToDelete) {
      const { id } = recordToDelete;
      dispatch(deleteContentLibrary({ id, coachId }));
      setConfimationModal(false);
      setRecordToDelete(null);
    }
  };

  const handleConfirmationModalClose = () => {
    setConfimationModal(false);
    setRecordToDelete(null);
  };
  // Handle form field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle language change
  const handleLanguageChange = (e) => {
    setFormData((prev) => ({ ...prev, language: e.target.value }));
  };

  // Handle file upload and convert to Base64
  const handleFileChange = (info) => {
    // max file size 50 MB
    const maxFileSize = 50 * 1024 * 1024;

    if (info.file.status === "done") {
      const file = info.file.originFileObj;

      if (file.size > maxFileSize) {
        message.error("File size should not exceed 50 MB.");
        return;
      }

      // Get file extension
      const extension = file.name.split(".").pop().toLowerCase();
      let mediaType = "";

      if (extension === "pdf") {
        mediaType = "PDF";
      } else if (["mp4", "webm", "ogg", "mov"].includes(extension)) {
        mediaType = "VIDEO";
      } else if (["mp3", "wav", "m4a"].includes(extension)) {
        mediaType = "AUDIO";
      } else {
        message.error("Unsupported file type");
        return;
      }

      // Create a FileReader to convert the file to Base64
      const reader = new FileReader();
      reader.onloadend = () => {
        let base64File = reader.result;

        if (base64File && base64File.startsWith("data:")) {
          base64File = base64File.split(",")[1];
        }

        // Update formData with file details
        setFormData((prev) => ({
          ...prev,
          fileName: file.name,
          mediaType: file.type,
          fileType: mediaType,
          fileBase64: base64File,
        }));
      };

      // Read the file as a data URL (Base64)
      reader.readAsDataURL(file);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setErrors({});
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Validate title
    if (!formData.title) {
      newErrors.title =
        trans("Common.title") + " " + trans("Common.isRequired");
    } else if (formData.title.trim().length < 3) {
      newErrors.title =
        trans("Common.title") + " " + trans("Common.isRequiredAndMinLength");
    }

    // Validate description
    if (!formData.description) {
      newErrors.description =
        trans("Common.description") + " " + trans("Common.isRequired");
    } else if (formData.description.trim().length < 3) {
      newErrors.description =
        trans("Common.description") +
        " " +
        trans("Common.isRequiredAndMinLength");
    }

    // Validate file upload
    if (!formData.fileBase64 && formData.fileName === null) {
      newErrors.file = trans("Common.file") + " " + trans("Common.isRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSave = () => {
    if (validateForm()) {
      setToastConfig(null);
      dispatch(createContentLibrary(formData));
      setShowModal(false);
    }
  };

  // Custom File Upload Handler
  const customRequest = ({ onSuccess }) => {
    onSuccess("ok");
  };

  // Handle search functionality
  const handleSearch = (value) => {
    if (value.trim() === "") {
      setFilteredData(contentLibrary);
    } else {
      setFilteredData(
        contentLibrary.filter((item) =>
          item.title.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  // Clear search filter
  const clearSearch = () => {
    setSearchText("");
    setFilteredData(contentLibrary);
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    handleSearch(value);
  };

  // Toast notification logic
  useEffect(() => {
    if (!toastConfig) {
      if (lastDeleteMessage) {
        setToastConfig({
          type: "success",
          message: lastDeleteMessage,
          heading: trans("Common.success"),
        });
        setTimeout(() => {
          dispatch(resetDeleteState());
        }, 3000);
      } else if (updateSuccess) {
        // Trigger success toast
        isEditMode
          ? setToastConfig({
              type: "success",
              message: trans("Toolbox.contentUpdatedSuccessfully"),
              heading: trans("Common.success"),
            })
          : setToastConfig({
              type: "success",
              message: trans("Toolbox.contentAddedSuccessfully"),
              heading: trans("Common.success"),
            });
      } else if (updateError) {
        // Trigger error toast
        isEditMode
          ? setToastConfig({
              type: "error",
              message: trans("Toolbox.contentUpdatedFailed"),
              heading: trans("Common.failed"),
            })
          : setToastConfig({
              type: "error",
              message: trans("Toolbox.contentAddedFailed"),
              heading: trans("Common.failed"),
            });
      } else if (contentLibraryError) {
        setToastConfig({
          type: "error",
          message: contentLibraryError,
          heading: trans("Common.failed"),
        });
      }
      // Reset the last delete message in the state
      setTimeout(() => {
        setToastConfig(null);
      }, 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    updateSuccess,
    updateError,
    trans,
    lastDeleteMessage,
    contentLibraryError,
    dispatch,
  ]);

  return (
    <div className=" formContainer pt-0 mb-6">
      {isContentLibraryLoading ? (
        <Loader />
      ) : (
        <div className="templateWrapper px-4 py-4 m-0 p-0">
          <Row className="pb-3">
            <Row>
              <Col>
                <h5 className="pb-2">
                  {trans("Toolbox.contentLibraryDetails")}
                </h5>
              </Col>
              <Col className="d-flex justify-content-end">
                <Button className="saveButton" onClick={handleAdd}>
                  <i className="bi bi-plus"></i>
                  {trans("Common.add") + " " + trans("Common.file")}
                </Button>
              </Col>
            </Row>
            <Row className="mb-1">
              <Col xs={12} md={6}>
                <div className="d-flex w-50 mb-3">
                  <Input
                    value={searchText}
                    onChange={handleChange}
                    placeholder={trans("Toolbox.searchByTitle")}
                    allowClear
                    onClear={clearSearch}
                    className="rounded-0 pb-0"
                  />
                </div>
              </Col>
            </Row>

            {/* Table */}
            <Row>
              <Table
                className="custom-table"
                columns={columns}
                dataSource={filteredData}
                pagination={false}
                rowKey={(record) => record.mediaId}
                locale={{
                  emptyText: contentLibraryError
                    ? trans("Toolbox.fetchError")
                    : trans("Toolbox.noResults"),
                }}
                components={{
                  table: (props) => (
                    <table
                      {...props}
                      style={{
                        ...props.style,
                        borderCollapse: "separate",
                        borderSpacing: "0 10px",
                      }}
                    />
                  ),
                  header: {
                    cell: (props) => {
                      return (
                        <th
                          {...props}
                          style={{
                            ...props.style,
                            background: "transparent",
                            borderColor: "#FCFBFFCC",
                            fontFamily: "Proxima-Nova-Regular",
                            borderStyle: "solid",
                            borderWidth: "8px 0px",
                            padding: "12px 16px",
                            // Border radius for header cells
                            borderRadius:
                              props.children === columns[0].title
                                ? "12px 0 0 12px"
                                : props.children ===
                                  columns[columns.length - 1].title
                                ? "0 12px 12px 0"
                                : "0",
                          }}
                        />
                      );
                    },
                    row: (props) => (
                      <tr
                        {...props}
                        style={{
                          ...props.style,
                          background: "transparent",
                        }}
                      />
                    ),
                  },
                  body: {
                    cell: (props) => (
                      <td
                        {...props}
                        style={{
                          ...props.style,
                          background: "transparent",
                          borderColor: "transparent",
                          borderStyle: "solid",
                          borderWidth: "8px 0px",
                          fontFamily: "Proxima-Nova-Regular",
                          padding: "12px 16px",
                          // Border radius for body cells
                          borderRadius:
                            props.index % columns.length === 0
                              ? "12px 0 0 12px"
                              : props.index % columns.length ===
                                columns.length - 1
                              ? "0 12px 12px 0"
                              : "0",
                        }}
                      />
                    ),
                    row: (props) => (
                      <tr
                        {...props}
                        style={{
                          ...props.style,
                          fontFamily: "Proxima-Nova-Regular",
                          background: "transparent",
                          margin: "10px 0",
                        }}
                      />
                    ),
                  },
                }}
              />
            </Row>
          </Row>
        </div>
      )}
      {/* Modal for adding/editing content */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {isEditMode
              ? trans("Common.edit")
              : trans("Common.add") + " " + trans("Common.file")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3">
              <Col xs={12} md={6}>
                <Form.Group controlId="title">
                  <FormLabel label={trans("Common.title")} required />
                  <FormControl
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFieldChange}
                    required
                    isInvalid={!!errors.title}
                    feedback={errors.title}
                  />
                </Form.Group>
              </Col>
              <Col xs={12} md={6}>
                <Form.Group controlId="language">
                  <FormLabel label={trans("Common.language")} required />
                  <Form.Select
                    name="language"
                    value={formData.language}
                    onChange={handleLanguageChange}
                    disabled={true}
                    required
                  >
                    {languages.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <Form.Group controlId="description">
                  <FormLabel label={trans("Common.description")} required />
                  <FormControlTextArea
                    placeholder={trans("Toolbox.enterNotes")}
                    name="description"
                    value={formData.description}
                    onChange={handleFieldChange}
                    rows={5}
                    isInvalid={!!errors.description}
                    feedback={errors.description}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col xs={12}>
                <FormLabel
                  label={`${trans("Common.file")} (Max: 50MB)`}
                  required
                />
                <div className="file-upload-box">
                  <Upload.Dragger
                    customRequest={customRequest}
                    onChange={handleFileChange}
                    showUploadList={false}
                    accept=".pdf,.mp4,.webm,.ogg,.mp3,.wav,.MOV,.m4a"
                    name="file"
                    beforeUpload={(file) => {
                      // Get file extension
                      const extension = file.name
                        .split(".")
                        .pop()
                        .toLowerCase();
                      const allowedExtensions = [
                        "pdf",
                        "mp4",
                        "webm",
                        "ogg",
                        "mp3",
                        "wav",
                        "mov",
                        "m4a",
                      ];

                      if (!allowedExtensions.includes(extension)) {
                        message.error(trans("Toolbox.fileTypeRestriction"));
                        return Upload.LIST_IGNORE;
                      }
                      return true;
                    }}
                  >
                    <p className="ant-upload-text">
                      {trans("Common.chooseFile")}
                    </p>
                    <p className="ant-upload-hint">
                      {trans("Common.dragYourFile")}
                    </p>
                  </Upload.Dragger>
                </div>
                {errors.file && (
                  <div className="text-danger">{errors.file}</div>
                )}
              </Col>
            </Row>

            {/* Show file name if selected */}
            {formData.fileName && (
              <Row className="mb-3">
                <Col xs={12}>
                  <div className="d-flex align-items-center">
                    <strong>
                      {trans("Common.selected") + " " + trans("Common.file")}:
                    </strong>
                    <span className="ml-2">{formData.fileName}</span>
                    <Button
                      type="button"
                      className="ml-2 p-0 bg-light rounded-circle border-0 d-flex justify-content-center align-items-center text-muted fs-4"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          fileName: null,
                          mediaType: "",
                          fileBase64: "",
                        })
                      }
                    >
                      <i className="bi bi-x"></i>
                    </Button>
                  </div>
                </Col>
              </Row>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleCloseModal}
            className="cancel-button w-25 "
          >
            {trans("Common.cancel")}
          </Button>
          <Button className="saveButton w-25 ml-2" onClick={handleSave}>
            {isEditMode ? trans("Common.update") : trans("Common.add")}
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal for Deletion */}
      <Modal
        show={confimationModal}
        onHide={handleConfirmationModalClose}
        centered
        aria-labelledby="contained-modal-title-vcenter"
      >
        <Modal.Header closeButton>
          <Modal.Title id="contained-modal-title-vcenter">
            {trans("Common.confirmation")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {trans("Common.areYouSureDeleteDocument") +
              " " +
              recordToDelete?.title +
              "?"}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={handleConfirmationModalClose}
            className="cancel-button w-25 p-1"
          >
            {trans("Common.cancel")}
          </Button>
          <Button
            className="saveButton w-25 ml-2"
            onClick={handleConfirmDelete}
          >
            {trans("Common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
      {toastConfig && (
        <ToastNotification
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ContentLibrary;
